/* iter55p11 — Public "Author Ticket Rail" for the landing page.
 *
 * DESIGN RATIONALE (why not a carousel):
 *   A carousel hides everyone except the currently-visible slide; new
 *   authors are discoverable only if the visitor waits or clicks. This
 *   pattern instead ships:
 *     · ONE large "Featured Author" spotlight on the left (fixed prominence,
 *       weekly rotation by word-count).
 *     · A row of SMALL, always-visible "author tickets" on the right
 *       (5-6 at once, arrow-scrolled). The library-card / train-ticket
 *       aesthetic matches Scriptorium's palette and the user's own
 *       "ticket" language, and every author is one glance away — no
 *       waiting for the carousel to rotate.
 *   On mobile the two columns stack (featured → tickets grid).
 *
 * Data:  GET /api/authors/featured (public, no auth)
 * Empty: renders NOTHING when there are 0 opted-in authors, so the
 *        landing page doesn't ship a hollow section.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Ticket, MessageCircle, User, ArrowRight } from 'lucide-react';
import { P, FONTS } from './scriptorium/palette';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function Portrait({ url, name, size = 12 }) {
  const cls = `h-${size} w-${size} overflow-hidden rounded-full border bg-muted shrink-0`;
  // Backend-proxied portraits come as `/api/profile/portrait/<id>` and
  // need REACT_APP_BACKEND_URL prepended so they resolve on preview + prod.
  const resolved = url && url.startsWith('/api/')
    ? ((process.env.REACT_APP_BACKEND_URL || '') + url)
    : url;
  return (
    <div className={cls} style={{ borderColor: `${P.gold}66` }}>
      {resolved
        ? <img src={resolved} alt={name} className="h-full w-full object-cover" />
        : <User className="h-full w-full p-3 text-muted-foreground" />}
    </div>
  );
}

function TicketCard({ author }) {
  const trackClick = () => {
    try {
      fetch(`${API}/analytics/author-ticket-click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pen_slug: author.pen_slug, source: 'landing-ticket' }),
        keepalive: true,
      });
    } catch { /* silent */ }
  };
  return (
    <a
      href={`/authors/${author.pen_slug}`}
      onClick={trackClick}
      data-testid={`landing-author-ticket-${author.pen_slug}`}
      className="group inline-flex w-[220px] shrink-0 flex-col rounded-md border p-3 transition hover:-translate-y-0.5 hover:shadow"
      style={{
        borderColor:     `${P.gold}55`,
        backgroundColor: P.paperDeep,
      }}
    >
      <div className="flex items-center gap-2">
        <Portrait url={author.portrait_url} name={author.pen_name} size={10} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-semibold" style={{ color: P.ink, fontFamily: FONTS.display }}>
            {author.pen_name}
          </div>
          <div className="text-[9.5px] tracking-wider uppercase" style={{ color: P.inkSoft, fontFamily: FONTS.type }}>
            {author.contact_open ? 'Accepting mail' : 'Author'}
          </div>
        </div>
      </div>
      <p className="mt-2 line-clamp-2 text-[11px] leading-relaxed" style={{ color: P.inkSoft }}>
        {author.bio_short || 'Working writer.'}
      </p>
      {author.sample_opt_in && (
        <div
          className="mt-2 inline-flex w-fit items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px]"
          style={{ backgroundColor: `${P.gold}44`, color: P.leather, fontFamily: FONTS.type }}
        >
          <Ticket className="h-3 w-3" />
          {author.sample_label || 'Sample available'}
        </div>
      )}
      {author.stats_opt_in && (
        <div
          className="mt-2 flex items-center gap-2 text-[10.5px]"
          style={{ color: P.inkSoft, fontFamily: FONTS.type }}
          data-testid={`landing-author-stats-${author.pen_slug}`}
        >
          <span className="inline-flex items-center gap-1">
            <span className="font-semibold" style={{ color: P.oxblood }}>
              {author.books_sold ?? 0}
            </span>
            sold
          </span>
          <span aria-hidden="true">·</span>
          <span className="inline-flex items-center gap-1">
            <span className="font-semibold" style={{ color: P.oxblood }}>
              {author.books_in_pipeline ?? 0}
            </span>
            in pipeline
          </span>
        </div>
      )}
    </a>
  );
}

export default function LandingAuthorTicketRail() {
  const [featured, setFeatured] = useState(null);
  const [tickets,  setTickets]  = useState([]);
  const [loaded,   setLoaded]   = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/authors/featured?limit=8`)
      .then((r) => { if (alive) { setFeatured(r.data?.featured || null); setTickets(r.data?.tickets || []); } })
      .catch(() => { /* silent — landing works without the rail */ })
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  if (!loaded)                             return null;
  if (!featured && tickets.length === 0)   return null;

  return (
    <section
      className="relative z-10 mx-auto max-w-6xl px-4 py-10"
      data-testid="landing-author-rail"
      style={{ color: P.ink }}
    >
      <div className="mb-4">
        <div
          className="text-[10.5px] uppercase tracking-[0.32em]"
          style={{ color: P.oxblood, fontFamily: FONTS.type }}
        >
          Meet our authors
        </div>
        <h2
          className="text-2xl tracking-tight"
          style={{ fontFamily: FONTS.display, fontWeight: 600 }}
        >
          Top Writers on The Loom
        </h2>
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_1fr]">
        {/* Featured author spotlight */}
        {featured && (
          <a
            href={`/authors/${featured.pen_slug}`}
            onClick={() => {
              try {
                fetch(`${API}/analytics/author-ticket-click`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ pen_slug: featured.pen_slug, source: 'landing-featured' }),
                  keepalive: true,
                });
              } catch { /* silent */ }
            }}
            data-testid="landing-author-featured"
            className="group relative flex flex-col overflow-hidden rounded-lg border p-5 transition hover:shadow-lg"
            style={{
              backgroundImage: `linear-gradient(140deg, ${P.paperDeep} 0%, ${P.paper} 70%, ${P.gold}22 100%)`,
              borderColor:     P.gold,
              boxShadow:       `0 12px 30px -18px ${P.leather}55`,
            }}
          >
            <div
              className="mb-2 inline-flex w-fit items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider"
              style={{ borderColor: P.gold, color: P.oxblood, fontFamily: FONTS.type }}
            >
              Featured this week
            </div>
            <div className="flex items-center gap-4">
              <Portrait url={featured.portrait_url} name={featured.pen_name} size={20} />
              <div className="min-w-0">
                <div className="truncate text-2xl" style={{ fontFamily: FONTS.display, fontWeight: 600 }}>
                  {featured.pen_name}
                </div>
                <div className="text-[10.5px] uppercase tracking-[0.28em]" style={{ color: P.oxblood, fontFamily: FONTS.type }}>
                  {featured.sample_opt_in ? 'Free sample available' : 'Working writer'}
                </div>
              </div>
            </div>
            <p className="mt-3 text-[13.5px] leading-relaxed" style={{ color: P.inkSoft }}>
              {featured.bio_short || 'A writer whose words go on the wall of the workshop.'}
            </p>
            {featured.sample_opt_in && (
              <div
                className="mt-3 inline-flex w-fit items-center gap-1 rounded-sm px-2 py-0.5 text-[11px] font-semibold"
                style={{ backgroundColor: `${P.gold}44`, color: P.leather, fontFamily: FONTS.type }}
              >
                <Ticket className="h-3 w-3" /> {featured.sample_label || 'Read the sample chapter'}
              </div>
            )}
            {featured.stats_opt_in && (
              <div
                className="mt-3 flex items-center gap-4 text-[12px]"
                style={{ color: P.inkSoft, fontFamily: FONTS.type }}
                data-testid="landing-author-featured-stats"
              >
                <span>
                  <span className="text-lg font-semibold" style={{ color: P.oxblood, fontFamily: FONTS.display }}>
                    {featured.books_sold ?? 0}
                  </span>{' '}books sold
                </span>
                <span aria-hidden="true" style={{ color: `${P.inkSoft}55` }}>·</span>
                <span>
                  <span className="text-lg font-semibold" style={{ color: P.oxblood, fontFamily: FONTS.display }}>
                    {featured.books_in_pipeline ?? 0}
                  </span>{' '}in the pipeline
                </span>
              </div>
            )}
            <div className="mt-4 flex items-center gap-4 text-[11px]" style={{ color: P.inkSoft, fontFamily: FONTS.type }}>
              {featured.contact_open && (
                <span className="inline-flex items-center gap-1">
                  <MessageCircle className="h-3 w-3" /> Fan mail welcome
                </span>
              )}
              <span className="inline-flex items-center gap-1 transition group-hover:translate-x-0.5">
                Visit page <ArrowRight className="h-3 w-3" />
              </span>
            </div>
          </a>
        )}

        {/* Ticket rail — everyone else, always visible */}
        <div>
          <div className="mb-2 text-[10px] uppercase tracking-[0.32em]" style={{ color: P.inkSoft, fontFamily: FONTS.type }}>
            Also on The Loom
          </div>
          <div className="-mx-1 flex snap-x snap-mandatory gap-3 overflow-x-auto px-1 pb-2">
            {tickets.map((a) => (
              <div key={a.pen_slug} className="snap-start">
                <TicketCard author={a} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
